import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MaskPipe } from 'ngx-mask';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

@Component({
  selector: 'app-dashboard-approval-professional',
  templateUrl: './dashboard-approval-professional.component.html',
  styleUrls: ['./dashboard-approval-professional.component.scss'],
})
export class DashboardApprovalProfessionalComponent
  implements OnInit, OnDestroy
{
  @Input() comparingVersions: boolean = false;
  data: ProfessionalResponse;
  mainData: ProfessionalResponse;
  comparingData: ProfessionalResponse;
  loading = true;
  mainLoading = false;
  comparingLoading = false;


  private eventMainVersion: any;
  private eventComparingVersion: any;
  private eventVersion: any;

  constructor(
    private maskPipe: MaskPipe,
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.professional) {
        this.data = this.approvalService.approvalData.professional;
        this.loading = false;
      } else {
        // Buscar dados da API v3 se não houver no cache
        this.getApprovalData();
      }
    }

    // Listener para mudanças de versão
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Incrementar contador de loading v3 quando iniciar requisição
        this.approvalService.incrementV3Loading();
        
        // Ativar loading em todas as abas quando trocar de versão
        this.loading = true;
        this.getApprovalData();
      }
    });

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnDestroy() {
    if (this.eventVersion) {
      this.eventVersion.unsubscribe();
    }
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = null;
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = null;
      this.comparingLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.getApprovalData('comparing');
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      // Obter register_uuid da rota
      const register_uuid = this.route.snapshot.params.id;
      
      if (!register_uuid) {
        if (versionType === 'main') {
          this.mainData = null;
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = null;
          this.comparingLoading = false;
        } else {
          this.data = null;
          this.loading = false;
        }
        this.cdr.detectChanges();
        return;
      }

      // Parâmetros da API v3
      // Se houver versão selecionada no approvalService, usar ela; caso contrário, usar last=true
      const params: any = {
        data_type: 'professional',
      };
      
      // Verificar se há versão específica selecionada
      let versionToUse = null;
      if (versionType === 'main' && this.approvalService.approvalData.mainVersion) {
        versionToUse = this.approvalService.approvalData.mainVersion;
      } else if (versionType === 'comparing' && this.approvalService.approvalData.comparingVersion) {
        versionToUse = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        versionToUse = this.approvalService.approvalData.version;
      }
      
      if (versionToUse) {
        params.version = versionToUse;
      } else {
        params.last = true;
      }

      // Chamar API v3
      const professionalResponse = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: params,
        token: true,
      });

      // Processar resposta da API v3
      if (professionalResponse && professionalResponse.data) {
        const professionalData = this.processProfessionalData(professionalResponse);

        if (versionType === 'main') {
          this.mainData = professionalData;
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = professionalData;
          this.comparingLoading = false;
          this.cdr.detectChanges();
        } else {
          this.data = professionalData;
          this.loading = false;
          
          // Armazenar no cache apenas se não estiver em modo de comparação
          if (!this.comparingVersions && professionalData) {
            this.approvalService.approvalData.professional = professionalData;
          }
        }
      } else {
        // Sem dados na resposta
        if (versionType === 'main') {
          this.mainData = null;
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = null;
          this.comparingLoading = false;
        } else {
          this.data = null;
          this.loading = false;
        }
        this.cdr.detectChanges();
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainData = null;
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingData = null;
        this.comparingLoading = false;
      } else {
        this.data = null;
        this.loading = false;
      }
      this.cdr.detectChanges();
    } finally {
      // Decrementar contador de loading v3 quando terminar requisição normal (não comparação)
      if (!versionType && !this.comparingVersions) {
        this.approvalService.decrementV3Loading();
      }
    }
  }

  /**
   * Processa os dados profissionais da API v3
   * Estrutura da resposta: data.versions.professional.versions[0].data
   */
  private processProfessionalData(response: any): ProfessionalResponse | null {
    // Extrair dados da estrutura da API v3
    const versionData = response?.data?.versions?.professional?.versions?.[0]?.data;
    
    if (!versionData) {
      return null;
    }

    // Retornar os dados no formato esperado
    return versionData as ProfessionalResponse;
  }

  getDataForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) {
      return this.data;
    }
    if (versionType === 'main') {
      return this.mainData;
    } else if (versionType === 'comparing') {
      return this.comparingData;
    }
    return null;
  }

  /**
   * Verifica se há dados profissionais válidos
   * Retorna true se pelo menos um campo importante tiver valor
   */
  hasProfessionalData(data: ProfessionalResponse | null): boolean {
    if (!data) {
      return false;
    }

    // Verificar se pelo menos um campo importante tem valor
    const hasDocumentNumber = !!data.document_number;
    const hasCorporateName = !!data.corporate_name;
    const hasOccupation = !!data.occupation;
    const hasCorporateEmail = !!data.corporate_email;
    const hasPhone = !!(data.phone && (data.phone.number || data.phone.phone));
    const hasAddress = !!data.address;
    const hasBranch = !!data.phone?.branch;

    return hasDocumentNumber || hasCorporateName || hasOccupation || 
           hasCorporateEmail || hasPhone || hasAddress || hasBranch;
  }

  getLoadingForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) {
      return this.loading;
    }
    if (versionType === 'main') {
      return this.mainLoading;
    } else if (versionType === 'comparing') {
      return this.comparingLoading;
    }
    return false;
  }

  // Função para comparar valores e retornar classe de cor (apenas para lado direito/comparing)
  getComparisonColor(
    mainValue: any,
    comparingValue: any,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    // Apenas aplicar cores no lado direito (comparing)
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const normalizeValue = (val: any): string => {
      if (val === null || val === undefined || val === '') return '';
      if (typeof val === 'boolean') return val ? 'sim' : 'não';
      if (typeof val === 'string') {
        const trimmed = val.toLowerCase().trim();
        // Normalizar valores comuns
        if (trimmed === 'sim' || trimmed === 'yes' || trimmed === 'true')
          return 'sim';
        if (
          trimmed === 'não' ||
          trimmed === 'nao' ||
          trimmed === 'no' ||
          trimmed === 'false'
        )
          return 'não';
        return trimmed;
      }
      return String(val).toLowerCase().trim();
    };

    const mainNorm = normalizeValue(mainValue);
    const comparingNorm = normalizeValue(comparingValue);

    // Verde: informação foi adicionada (esquerdo não tem, direito tem)
    if (!mainNorm && comparingNorm) {
      return 'text-success'; // Verde
    }

    // Vermelho: informação foi removida (esquerdo tem, direito não tem)
    if (mainNorm && !comparingNorm) {
      return 'text-danger'; // Vermelho
    }

    // Amarelo: valores são diferentes (ex: esquerdo "sim", direito "não")
    if (mainNorm && comparingNorm && mainNorm !== comparingNorm) {
      return 'text-warning'; // Amarelo
    }

    return '';
  }

  // Função helper para obter valor do main quando estiver no contexto comparing
  getMainValueForComparison(path: string): any {
    if (!this.comparingVersions || !this.mainData) return null;

    const keys = path.split('.');
    let value: any = this.mainData;

    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  // Função helper para obter valor do comparing quando estiver no contexto comparing
  getComparingValueForComparison(path: string): any {
    if (!this.comparingVersions || !this.comparingData) return null;

    const keys = path.split('.');
    let value: any = this.comparingData;

    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  // Função helper para obter cor de comparação usando caminho de propriedade
  getComparisonColorByPath(
    path: string,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const mainValue = this.getMainValueForComparison(path);
    const comparingValue = this.getComparingValueForComparison(path);

    return this.getComparisonColor(mainValue, comparingValue, versionType);
  }

  // Função específica para comparar endereços
  getAddressComparisonColor(
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const mainAddress = this.mainData?.address;
    const comparingAddress = this.comparingData?.address;

    if (!mainAddress && comparingAddress) {
      return 'text-success'; // Endereço foi adicionado
    }

    if (mainAddress && !comparingAddress) {
      return 'text-danger'; // Endereço foi removido
    }

    if (!mainAddress && !comparingAddress) {
      return ''; // Ambos não têm endereço
    }

    // Comparar campos individuais do endereço
    const fields = [
      'cep',
      'public_place',
      'number',
      'complement',
      'district',
      'city',
      'uf',
    ];
    let hasDifference = false;

    for (const field of fields) {
      const mainValue = mainAddress[field];
      const comparingValue = comparingAddress[field];
      if (mainValue !== comparingValue) {
        hasDifference = true;
        break;
      }
    }

    return hasDifference ? 'text-warning' : '';
  }

  // Função específica para comparar telefone
  getPhoneComparisonColor(
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const mainPhone = this.mainData?.phone;
    const comparingPhone = this.comparingData?.phone;

    if (!mainPhone && comparingPhone) {
      return 'text-success'; // Telefone foi adicionado
    }

    if (mainPhone && !comparingPhone) {
      return 'text-danger'; // Telefone foi removido
    }

    if (!mainPhone && !comparingPhone) {
      return ''; // Ambos não têm telefone
    }

    // Comparar campos do telefone
    const mainNumber = mainPhone.number || mainPhone.phone;
    const comparingNumber = comparingPhone.number || comparingPhone.phone;
    const mainCode = mainPhone.code;
    const comparingCode = comparingPhone.code;

    if (mainNumber !== comparingNumber || mainCode !== comparingCode) {
      return 'text-warning';
    }

    return '';
  }

  // Função para determinar o status de um campo (added, removed, modified, unchanged)
  getFieldStatus(path: string): 'added' | 'removed' | 'modified' | 'unchanged' {
    if (!this.comparingVersions || !this.mainData || !this.comparingData) {
      return 'unchanged';
    }

    const mainValue = this.getMainValueForComparison(path);
    const comparingValue = this.getComparingValueForComparison(path);

    const normalizeValue = (val: any): string => {
      if (val === null || val === undefined || val === '') return '';
      if (typeof val === 'boolean') return val ? 'sim' : 'não';
      if (typeof val === 'string') {
        return val.toLowerCase().trim();
      }
      return String(val).toLowerCase().trim();
    };

    const mainNorm = normalizeValue(mainValue);
    const comparingNorm = normalizeValue(comparingValue);

    // Adicionado: esquerdo não tem, direito tem
    if (!mainNorm && comparingNorm) {
      return 'added';
    }

    // Removido: esquerdo tem, direito não tem
    if (mainNorm && !comparingNorm) {
      return 'removed';
    }

    // Modificado: ambos têm mas são diferentes
    if (mainNorm && comparingNorm && mainNorm !== comparingNorm) {
      return 'modified';
    }

    return 'unchanged';
  }

  // Função para obter o valor a ser exibido (se for removed, mostra o valor do main)
  getFieldValueForDisplay(
    path: string,
    currentValue: any,
    versionType: 'main' | 'comparing' | null = null
  ): any {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return currentValue;
    }

    const status = this.getFieldStatus(path);
    if (status === 'removed') {
      // Se foi removido, mostrar o valor original do main
      return this.getMainValueForComparison(path);
    }

    return currentValue;
  }

  // Função para verificar se o campo deve ter strikethrough
  getFieldStrikethroughClass(
    path: string,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getFieldStatus(path);
    if (status === 'removed') {
      return 'text-decoration-line-through';
    }

    return '';
  }

  // Função para retornar o ícone HTML baseado no status do campo
  getFieldComparisonIcon(
    path: string,
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getFieldStatus(path);

    switch (status) {
      case 'added':
        return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="check" />';
      case 'removed':
        return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="error" />';
      case 'modified':
        return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="warning" />';
      default:
        return '';
    }
  }

  // Função específica para comparar endereço completo (retorna status)
  getAddressStatus(): 'added' | 'removed' | 'modified' | 'unchanged' {
    if (!this.comparingVersions || !this.mainData || !this.comparingData) {
      return 'unchanged';
    }

    const mainAddress = this.mainData?.address;
    const comparingAddress = this.comparingData?.address;

    if (!mainAddress && comparingAddress) {
      return 'added';
    }

    if (mainAddress && !comparingAddress) {
      return 'removed';
    }

    if (!mainAddress && !comparingAddress) {
      return 'unchanged';
    }

    // Comparar campos individuais do endereço
    const fields = [
      'cep',
      'public_place',
      'number',
      'complement',
      'district',
      'city',
      'uf',
    ];

    for (const field of fields) {
      const mainValue = mainAddress[field];
      const comparingValue = comparingAddress[field];
      if (mainValue !== comparingValue) {
        return 'modified';
      }
    }

    return 'unchanged';
  }

  // Função para obter o valor do endereço a ser exibido (se for removed, mostra o valor do main)
  getAddressValueForDisplay(
    currentAddress: any,
    versionType: 'main' | 'comparing' | null = null
  ): any {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return currentAddress;
    }

    const status = this.getAddressStatus();
    if (status === 'removed') {
      // Se foi removido, mostrar o endereço original do main
      return this.mainData?.address;
    }

    return currentAddress;
  }

  // Função para verificar se o endereço deve ter strikethrough
  getAddressStrikethroughClass(
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getAddressStatus();
    if (status === 'removed') {
      return 'text-decoration-line-through';
    }

    return '';
  }

  // Função para retornar o ícone HTML do endereço
  getAddressComparisonIcon(
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getAddressStatus();

    switch (status) {
      case 'added':
        return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="check" />';
      case 'removed':
        return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="error" />';
      case 'modified':
        return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="warning" />';
      default:
        return '';
    }
  }

  // Função específica para comparar telefone (retorna status)
  getPhoneStatus(): 'added' | 'removed' | 'modified' | 'unchanged' {
    if (!this.comparingVersions || !this.mainData || !this.comparingData) {
      return 'unchanged';
    }

    const mainPhone = this.mainData?.phone;
    const comparingPhone = this.comparingData?.phone;

    if (!mainPhone && comparingPhone) {
      return 'added';
    }

    if (mainPhone && !comparingPhone) {
      return 'removed';
    }

    if (!mainPhone && !comparingPhone) {
      return 'unchanged';
    }

    // Comparar campos do telefone
    const mainNumber = mainPhone.number || mainPhone.phone;
    const comparingNumber = comparingPhone.number || comparingPhone.phone;
    const mainCode = mainPhone.code;
    const comparingCode = comparingPhone.code;

    if (mainNumber !== comparingNumber || mainCode !== comparingCode) {
      return 'modified';
    }

    return 'unchanged';
  }

  // Função para obter o valor do telefone a ser exibido (se for removed, mostra o valor do main)
  getPhoneValueForDisplay(
    currentPhone: any,
    versionType: 'main' | 'comparing' | null = null
  ): any {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return currentPhone;
    }

    const status = this.getPhoneStatus();
    if (status === 'removed') {
      // Se foi removido, mostrar o telefone original do main
      return this.mainData?.phone;
    }

    return currentPhone;
  }

  // Função para verificar se o telefone deve ter strikethrough
  getPhoneStrikethroughClass(
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getPhoneStatus();
    if (status === 'removed') {
      return 'text-decoration-line-through';
    }

    return '';
  }

  // Função para retornar o ícone HTML do telefone
  getPhoneComparisonIcon(
    versionType: 'main' | 'comparing' | null = null
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getPhoneStatus();

    switch (status) {
      case 'added':
        return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="check" />';
      case 'removed':
        return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="error" />';
      case 'modified':
        return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="warning" />';
      default:
        return '';
    }
  }

  formatCnpj(value: string) {
    const newValue = this.maskPipe.transform(value, '00.000.000/0000-00');

    return newValue;
  }

  formatPhone(phone) {
    const number = phone.phone ?? phone.number;
    return `+${String(phone.code)} ${this.maskPipe.transform(
      number,
      number.length === 11 ? '(00) 00000-0000' : '(00) 0000-0000'
    )}`;
  }

  formatAddress(address: AddressResponse) {
    if (!address.cep) {
      return '-';
    }

    return `${address.public_place}, ${address.number} - ${address.complement}, ${address.district} - ${address.city}/${address.uf} Brasil`;
  }

  //   renderValue(parentPath: string[], key) {
  //     return Utils.renderValue(parentPath, key, this.approval);
  //   }
}
